'use client';

import * as React from 'react';
import { useFieldControlProps_unstable } from '@fluentui/react-field';
import { useActiveDescendant } from '@fluentui/react-aria';
import { ChevronDownRegular as ChevronDownIcon, DismissRegular as DismissIcon } from '@fluentui/react-icons';
import { useFluent_unstable as useFluent } from '@fluentui/react-shared-contexts';
import {
  getPartitionedNativeProps,
  mergeCallbacks,
  useMergedRefs,
  slot,
  useEventCallback,
  useOnClickOutside,
} from '@fluentui/react-utilities';
import { useComboboxBaseState } from '../../utils/useComboboxBaseState';
import { useComboboxPositioning } from '../../utils/useComboboxPositioning';
import { Listbox } from '../Listbox/Listbox';
import type { DropdownBaseProps, DropdownBaseState, DropdownProps, DropdownState } from './Dropdown.types';
import { useListboxSlot } from '../../utils/useListboxSlot';
import { useButtonTriggerSlot } from './useButtonTriggerSlot';
import { optionClassNames } from '../Option/useOptionStyles.styles';
import type { ComboboxOpenEvents } from '../Combobox/Combobox.types';

/**
 * Create the base state required to render Dropdown, without design-only props.
 *
 * @param props - props from this instance of Dropdown (without appearance and size)
 * @param ref - reference to root HTMLButtonElement of Dropdown
 */
export const useDropdownBase_unstable = (
  props: DropdownBaseProps,
  ref: React.Ref<HTMLButtonElement>,
): DropdownBaseState => {
  'use no memo';

  // Merge props from surrounding <Field>, if any
  props = useFieldControlProps_unstable(props, { supportsLabelFor: true });
  const {
    listboxRef: activeDescendantListboxRef,
    activeParentRef,
    controller: activeDescendantController,
  } = useActiveDescendant<HTMLButtonElement, HTMLDivElement>({
    matchOption: el => el.classList.contains(optionClassNames.root),
  });

  const dropdownInternalState = useComboboxBaseState({ ...props, activeDescendantController, freeform: false });
  const { appearance: _appearance, size: _size, freeform: _freeform, ...baseState } = dropdownInternalState;
  const { clearable, clearSelection, disabled, hasFocus, multiselect, open, selectedOptions, setOpen } = baseState;

  const { primary: triggerNativeProps, root: rootNativeProps } = getPartitionedNativeProps({
    props,
    primarySlotTagName: 'button',
    excludedPropNames: ['children'],
  });

  const [comboboxPopupRef, comboboxTargetRef] = useComboboxPositioning(props);

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listbox = useListboxSlot(props.listbox, useMergedRefs(comboboxPopupRef, activeDescendantListboxRef), {
    state: dropdownInternalState,
    triggerRef,
    defaultProps: {
      children: props.children,
    },
  });

  const { targetDocument } = useFluent();

  useOnClickOutside({
    element: targetDocument,
    callback: event => setOpen(event as unknown as ComboboxOpenEvents, false),
    refs: [triggerRef, comboboxPopupRef, comboboxTargetRef],
    disabled: !open,
  });

  const trigger = useButtonTriggerSlot(props.button ?? {}, useMergedRefs(triggerRef, activeParentRef, ref), {
    state: dropdownInternalState,
    defaultProps: {
      type: 'button',
      // tabster navigation breaks if the button is disabled and tabIndex is 0
      tabIndex: triggerNativeProps.disabled ? undefined : 0,
      children: baseState.value || props.placeholder,
      'aria-controls': open ? listbox?.id : undefined,
      ...triggerNativeProps,
    },
    activeDescendantController,
  });

  const rootSlot = slot.always(props.root, {
    defaultProps: {
      'aria-owns': !props.inlinePopup && open ? listbox?.id : undefined,
      children: props.children,
      ...rootNativeProps,
    },
    elementType: 'div',
  });
  rootSlot.ref = useMergedRefs(rootSlot.ref, comboboxTargetRef);

  const showClearButton = selectedOptions.length > 0 && !disabled && clearable && !multiselect;
  const state: DropdownBaseState = {
    components: { root: 'div', button: 'button', clearButton: 'button', expandIcon: 'span', listbox: Listbox },
    root: rootSlot,
    button: trigger,
    listbox: open || hasFocus ? listbox : undefined,
    clearButton: slot.optional(props.clearButton, {
      defaultProps: {
        'aria-label': 'Clear selection',
        // Safari doesn't allow to focus an element with this
        // when the element is not visible (display: none) we need to remove it to avoid tabster issues
        tabIndex: showClearButton ? 0 : undefined,
        type: 'button',
      },
      elementType: 'button',
      renderByDefault: true,
    }),
    expandIcon: slot.optional(props.expandIcon, {
      renderByDefault: true,
      elementType: 'span',
    }),
    placeholderVisible: !baseState.value && !!props.placeholder,
    showClearButton,
    activeDescendantController,
    ...baseState,
  };

  const onClearButtonClick = useEventCallback(
    mergeCallbacks(state.clearButton?.onClick, (ev: React.MouseEvent<HTMLButtonElement>) => {
      clearSelection(ev);
      triggerRef.current?.focus();
    }),
  );

  if (state.clearButton) {
    state.clearButton.onClick = onClearButtonClick;
  }

  // Heads up! We don't support "clearable" in multiselect mode, so we should never display a slot
  if (multiselect) {
    state.clearButton = undefined;
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- "process.env" does not change in runtime
    React.useEffect(() => {
      if (clearable && multiselect) {
        // eslint-disable-next-line no-console
        console.error(`[@fluentui/react-combobox] "clearable" prop is not supported in multiselect mode.`);
      }
    }, [clearable, multiselect]);
  }

  return state;
};

/**
 * Create the state required to render Dropdown.
 *
 * The returned state can be modified with hooks such as useDropdownStyles_unstable,
 * before being passed to renderDropdown_unstable.
 *
 * @param props - props from this instance of Dropdown
 * @param ref - reference to root HTMLElement of Dropdown
 */
export const useDropdown_unstable = (props: DropdownProps, ref: React.Ref<HTMLButtonElement>): DropdownState => {
  'use no memo';

  const { appearance = 'outline', size = 'medium', ...baseProps } = props;
  const baseState = useDropdownBase_unstable(baseProps, ref);

  if (baseState.clearButton) {
    baseState.clearButton.children ??= <DismissIcon />;
  }

  if (baseState.expandIcon) {
    baseState.expandIcon.children ??= <ChevronDownIcon />;
  }

  return {
    ...baseState,
    appearance,
    size,
  };
};
